import { getLogger } from '@jitsi/logger';
import { v4 as uuidv4 } from 'uuid';

import { sendStatsToKibana } from '../../components/analytics/kibana/actions';
import { KIBANA_MESSAGES } from '../../components/analytics/kibana/constants';
import {
    dataChanelClosed,
    dataChanelOpened,
    setControlledUserID,
    setDefaultStoreControlSharing,
    setLaunchedUserID,
    setStatusControl
} from '../../components/wildix-remote-control/actions';
import {
    CONTROL_TIMEOUTS,
    REQUEST_TYPES, STATUSES,
    WEBRTC_TYPES,
    WISERVICE_TYPES
} from '../../components/wildix-remote-control/constants';
import {
    prepareDataToSend, sendDataToWIService
} from '../../components/wildix-remote-control/functions';
import { stunConfigCredentials } from '../../modules/shareData';
import WildixEvents from '../../service/WildixEvents';
import WildixXMPPEvents from '../../service/xmpp/WildixXMPPEvents';
const logger = getLogger(__filename);


export default class ControlManager {
    constructor() {
        logger.warn('controlManager');

        this.peerConnection = null;
        this.dataChannel = null;
        this.dispatch = null;
        this.myUserID = null;
        this.uuid = null;

        this.#initEvents();
    }

    #initEvents() {
        const conferenceRoom = APP.conference.__getChatRoom();

        this.dispatch = APP.store.dispatch;
        this.getState = APP.store.getState;
        this.myUserID = APP.conference.getMyUserId();


        APP.wildix.eventEmitter.on(WildixEvents.CONFERENCE_DISPOSE, this.exitFromConference.bind(this));
        if (conferenceRoom) {
            conferenceRoom.addEventListener(WildixXMPPEvents.CONTROL_SHARING_DATA, this.eventFromPresence.bind(this));
        }
    }

    checkIsPeerConnectionStuck() {
        setTimeout(() => {
            if (this.dataChannel) {
                if (this.isReadyStateFailed || this.isReadyStateConnecting) {
                    const { launchedControlID, controlledUserId } = this.getState()['wildix/features/control-sharing'];

                    this.dispatch(dataChanelClosed(REQUEST_TYPES.CONTROL_NOT_ESTABLISHED, true));
                    this.dispatch(sendStatsToKibana([ {
                        launchedControlID,
                        controlledUserId
                    } ], KIBANA_MESSAGES.RemoteControlDataStuck));
                }
            }
        }, CONTROL_TIMEOUTS.peerConnectionStuckDelay);
    }

    eventFromPresence(dataObject) {
        const { status, launchedControlID, controlledUserId } = JSON.parse(dataObject);

        if (status === STATUSES.CLOSED) {
            logger.warn('CONTROL_CLOSE pres');
            this.dispatch(setDefaultStoreControlSharing());
        } else if (status === STATUSES.OPENED) {
            logger.warn('CONTROL_OPENED pres');
            this.dispatch(setStatusControl(STATUSES.OPENED));
            this.dispatch(setLaunchedUserID(launchedControlID));
            this.dispatch(setControlledUserID(controlledUserId));
        }
    }

    exitFromConference() {
        if (this.dataChannel) {
            this.disconnectAndSendEvent();
        }
    }

    initDataChanel() {
        this.uuid = uuidv4();
        this.createPeerConnection(true);
        this.checkIsPeerConnectionStuck();
    }

    sendSDPPresence(object) {
        const preparedData = this.prepareDataToSDP(object);
        const { conference } = this.getState()['features/base/conference'];
        const { launchedControlID, controlledUserId, isUserAdmin } = this.getState()['wildix/features/control-sharing'];

        if (isUserAdmin) {
            conference.sendPrivateTextMessage(controlledUserId, preparedData, WEBRTC_TYPES.TYPE_MESSAGE);
        } else {
            conference.sendPrivateTextMessage(launchedControlID, preparedData, WEBRTC_TYPES.TYPE_MESSAGE);
        }
    }

    // Start the WebRTC Connection
    // We're either the caller (when we click 'connect' on our page)
    // Or the receiver (when the other page clicks 'connect'
    // and we recieve a signalling message through the websocket server)

    createPeerConnection(isCaller) {
        const { urls, username, password } = stunConfigCredentials;

        this.peerConnection = new RTCPeerConnection({
            iceServers: [
                { url: 'stun:stun.l.google.com:19302' },
                { url: 'stun:stun1.l.google.com:19302' },
                { url: 'stun:stun2.l.google.com:19302' },
                { url: 'stun:stun3.l.google.com:19302' },
                { url: 'stun:conference-turn.wildix.com:443' },
                {
                    'urls': urls,
                    'username': username,
                    'credential': password
                }
            ]
        });
        logger.warn(this.peerConnection, 'peerConnection');
        logger.warn(stunConfigCredentials, 'stunConfigCredentials');
        this.peerConnection.onicecandidate = this.gotIceCandidate.bind(this);

        // If we're the caller, we create the Data Channel
        // Otherwise, it opens for us and we receive an event as soon as the this.peerConnection opens
        if (isCaller) {
            this.dataChannel = this.peerConnection.createDataChannel('controlSharingChannel');
            this.dataChannel.onmessage = this.handleDataChannelReceiveMessage.bind(this);
            this.dataChannel.onopen = this.handleDataChannelStatusChange.bind(this);
            this.dataChannel.onclose = this.handleDataChannelStatusChange.bind(this);
        } else {
            this.peerConnection.ondatachannel = this.handleDataChannelCreated.bind(this);
        }

        // Kick it off (if we're the caller)
        if (isCaller) {
            this.peerConnection.createOffer()
            .then(offer => this.peerConnection.setLocalDescription(offer))
            .then(() => logger.warn('set local offer description'))
            .then(() => this.sendSDPPresence({ 'sdp': this.peerConnection.localDescription }))
            .then(() => logger.warn('sent offer description to remote'))
            .catch(this.errorHandler);
        }

        this.dispatch(sendStatsToKibana([ {
            urls,
            username,
            password,
            userID: this.myUserID
        } ], KIBANA_MESSAGES.RemoteControlDataSTUNCred));
    }

    // Handle messages from the Websocket signalling server

    gotMessageFromServer(signal) {
        // If we haven't started WebRTC, now's the time to do it
        // We must be the receiver then (ie not the caller)
        if (!this.peerConnection) {
            this.createPeerConnection(false);
        }

        logger.warn('signal: ', signal);

        // Ignore messages from ourself
        if (signal.uuid === this.uuid) {
            return;
        }

        if (signal.sdp) {
            this.peerConnection.setRemoteDescription(new RTCSessionDescription(signal.sdp))
            .then(() => logger.warn('set remote description'))
            .then(() => {
                // Only create answers in response to offers
                if (signal.sdp.type === WEBRTC_TYPES.OFFER) {
                    logger.warn('got offer');

                    this.peerConnection.createAnswer()
                    .then(answer => this.peerConnection.setLocalDescription(answer))
                    .then(() => logger.warn('set local answer description'))
                    .then(() => this.sendSDPPresence({ 'sdp': this.peerConnection.localDescription }))
                    .then(() => logger.warn('sent answer description to remote'))
                    .catch(this.errorHandler);
                }
            })
            .catch(this.errorHandler);
        } else if (signal.ice) {
            // logger.warn('received ice candidate from remote');
            this.peerConnection.addIceCandidate(new RTCIceCandidate(signal.ice))
            .then(() => logger.warn('added ice candidate'))
            .catch(this.errorHandler);
        }
    }

    prepareDataToSDP(data) {
        return JSON.stringify({
            type: WEBRTC_TYPES.CONTROL_OFFER,
            'uuid': this.uuid,
            ...data
        });
    }

    gotIceCandidate(event) {
        if (event.candidate !== null) {
            this.sendSDPPresence({ 'ice': event.candidate });
        }
    }

    // Called when we are not the caller (ie we are the receiver)
    // and the data channel has been opened
    handleDataChannelCreated(event) {
        this.dataChannel = event.channel;
        this.dataChannel.onmessage = this.handleDataChannelReceiveMessage.bind(this);
        this.dataChannel.onopen = this.handleDataChannelStatusChange.bind(this);
        this.dataChannel.onclose = this.handleDataChannelStatusChange.bind(this);
    }

    get isReadyStateOpen() {
        return this.dataChannel?.readyState === 'open';
    }

    get isReadyStateFailed() {
        return this.dataChannel?.readyState === 'failed';
    }
    get isReadyStateConnecting() {
        return this.dataChannel?.readyState === 'connecting';
    }

    // Handles clicks on the "Send" button by transmitting

    // a message to the remote peer.

    sendMessageThroughDataChannel(message = '') {
        // logger.warn('sending: ', message);
        const preparedData = prepareDataToSend(message);

        if (this.isReadyStateOpen) {
            this.dataChannel.send(preparedData);
        }
    }

    // Handle status changes on the local end of the data
    // channel; this is the end doing the sending of data
    // in this example.

    handleDataChannelStatusChange(event) {
        if (!this.dataChannel) {
            return;
        }

        logger.warn('this.dataChannel status: ', this.dataChannel.readyState);

        if (this.isReadyStateOpen) {
            this.dispatch(dataChanelOpened());
        } else {
            this.dispatch(dataChanelClosed());
        }
    }

    // Handle onmessage events for the data channel.
    // These are the data messages sent by the remote channel.

    handleDataChannelReceiveMessage(event) {
        const objectData = JSON.parse(event.data);

        // logger.warn('Message from user: ', event.data);

        if (objectData.closeDataChanel) {
            this.disconnectPeers();
            this.dispatch(dataChanelClosed());

            return;
        } else if (this.myUserID === objectData.id) {
            logger.warn('message from controlled user');

            return;
        } else if (objectData.message === WISERVICE_TYPES.messageMouseMove) {
            sendDataToWIService(objectData.message, objectData.movePosition, false);

            return;
        }


        sendDataToWIService(WISERVICE_TYPES.messageType, {
            msgdata: {
                'command': WISERVICE_TYPES.commandAction,
                controlData: objectData
            }
        });
    }

    disconnectAndSendEvent() {
        this.sendMessageThroughDataChannel({ closeDataChanel: true });
        this.disconnectPeers();
    }

    disconnectPeers() {

        // Close the RTCDataChannel if it's open.
        this.dataChannel?.close();

        // Close the RTCPeerConnection
        this.peerConnection?.close();

        this.dataChannel = null;
        this.peerConnection = null;
        this.uuid = null;
    }

    errorHandler(error) {
        logger.warn(error, 'errorHandler');
    }
}
